import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';
import { subscribeNewsletter } from '../lib/api';
import { useT } from '../lib/i18n';

const Footer = () => {
  const { t, lang } = useT();
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | already | error

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || state === 'sending') return;
    setState('sending');
    try {
      const { data } = await subscribeNewsletter(email.trim(), lang);
      setState(data.already_subscribed ? 'already' : 'success');
      setEmail('');
    } catch (_) {
      setState('error');
    }
  };

  return (
    <footer className="bg-ivory border-t-4 border-gold">
      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* Newsletter signup — present on every page */}
        <div className="mb-16 pb-14 border-b-2 border-gold/40 flex flex-col md:flex-row md:items-center md:justify-between gap-8" data-testid="footer-newsletter">
          <div className="md:max-w-md">
            <p className="text-xs tracking-[0.4em] text-dark-crimson uppercase mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('newsletter.eyebrow')}
            </p>
            <h3 className="text-3xl font-bold text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.05em' }}>
              {t('newsletter.title')}
            </h3>
            <p className="text-charcoal/70 text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('newsletter.privacy')}
            </p>
          </div>
          {state === 'success' || state === 'already' ? (
            <p data-testid="footer-newsletter-confirmation" className="text-dark-green text-lg italic md:w-96" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t(state === 'success' ? 'newsletter.success' : 'newsletter.already')}
            </p>
          ) : (
            <div className="md:w-96">
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-stretch gap-3" data-testid="footer-newsletter-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('newsletter.placeholder')}
                  disabled={state === 'sending'}
                  data-testid="footer-newsletter-email"
                  className="flex-1 bg-cream text-dark-green border-2 border-gold/60 rounded-none px-4 py-3 focus:border-dark-crimson focus:outline-none"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}
                />
                <button
                  type="submit"
                  disabled={state === 'sending'}
                  data-testid="footer-newsletter-submit"
                  className="bg-dark-crimson hover:bg-crimson text-cream px-6 py-3 rounded-none border-2 border-gold text-sm tracking-[0.2em] font-semibold disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  {state === 'sending' ? t('newsletter.subscribing') : t('newsletter.cta')}
                </button>
              </form>
              {state === 'error' && (
                <p data-testid="footer-newsletter-error" className="text-dark-crimson text-sm mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('newsletter.error')}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 rotate-45 bg-gold border border-dark-crimson"></div>
              <h3 className="text-3xl font-bold text-dark-green" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
                CHIMERA
              </h3>
              <div className="w-2 h-2 rotate-45 bg-dark-crimson border border-gold"></div>
            </div>
            <p className="text-charcoal mb-8 leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>
              {t('footer.tagline')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61550130373016"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-12 h-12 bg-dark-crimson/10 border-2 border-gold/50 flex items-center justify-center hover:bg-dark-crimson hover:border-gold transition-all duration-300 group"
              >
                <Facebook className="text-dark-green group-hover:text-cream transition-colors" size={20} />
              </a>
              <a
                href="https://www.instagram.com/chimera_part_2/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-12 h-12 bg-dark-crimson/10 border-2 border-gold/50 flex items-center justify-center hover:bg-dark-crimson hover:border-gold transition-all duration-300 group"
              >
                <Instagram className="text-dark-green group-hover:text-cream transition-colors" size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-bold text-dark-green mb-6 border-b-2 border-dark-crimson pb-3 inline-block" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>
              <li>
                <Link to="/gallery" className="text-charcoal hover:text-dark-crimson transition-colors font-semibold">
                  {t('nav.gallery')}
                </Link>
              </li>
              <li>
                <Link to="/annex" className="text-charcoal hover:text-dark-crimson transition-colors font-semibold">
                  {t('nav.annex')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-charcoal hover:text-dark-crimson transition-colors font-semibold">
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-charcoal hover:text-dark-crimson transition-colors font-semibold">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* France Location */}
          <div>
            <h4 className="text-xl font-bold text-dark-green mb-6 border-b-2 border-dark-crimson pb-3 inline-block" style={{ fontFamily: 'Playfair Display, serif' }}>
              Montolieu
            </h4>
            <ul className="space-y-4 text-charcoal" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.95rem' }}>
              <li className="flex items-start gap-3">
                <MapPin className="text-dark-crimson mt-1 flex-shrink-0" size={18} />
                <span>167 Chemin De Franc<br />11170, France</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="text-dark-crimson flex-shrink-0" size={18} />
                <span className="text-gold font-semibold">+33 6 60 58 91 50</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="text-dark-crimson flex-shrink-0" size={18} />
                <span className="text-gold font-semibold">chimera_gallery@yahoo.ie</span>
              </li>
            </ul>
          </div>

          {/* Ireland Location */}
          <div>
            <h4 className="text-xl font-bold text-dark-green mb-6 border-b-2 border-dark-crimson pb-3 inline-block" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mullingar
            </h4>
            <ul className="space-y-4 text-charcoal" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.95rem' }}>
              <li className="flex items-start gap-3">
                <MapPin className="text-dark-crimson mt-1 flex-shrink-0" size={18} />
                <span>30-32 Oliver Plunket Street<br />Mullingar, Ireland</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="text-dark-crimson flex-shrink-0" size={18} />
                <span className="text-gold font-semibold">+353 87 803 83 57</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="text-dark-crimson flex-shrink-0" size={18} />
                <span className="text-gold font-semibold">chimera_gallery@yahoo.ie</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-10 border-t-2 border-gold/50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-charcoal/70 text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('footer.copyright')}
            </p>
            <div className="flex gap-8 text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <a href="#" className="text-charcoal/70 hover:text-dark-crimson transition-colors font-semibold">{t('footer.privacy')}</a>
              <a href="#" className="text-charcoal/70 hover:text-dark-crimson transition-colors font-semibold">{t('footer.terms')}</a>
              <a href="#" className="text-charcoal/70 hover:text-dark-crimson transition-colors font-semibold">{t('footer.cookies')}</a>
            </div>
          </div>
        </div>
      </div>

      {/* Art Deco Decorative Border */}
      <div className="h-3 bg-gradient-to-r from-dark-crimson via-gold to-dark-crimson"></div>
    </footer>
  );
};

export default Footer;
